import { useCallback, useRef, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import DownloadIcon from "@mui/icons-material/Download";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelScheduleSendIcon from "@mui/icons-material/CancelScheduleSend";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { getETaxDocuments } from "../services";
import {
  ETaxDocument,
  ETaxDocumentFilter,
  ETaxDocumentStatusCounts,
} from "../types/e-tax.type";
import { formatDate } from "../utils/date";
import { formatNumber } from "../utils";
import { useDebounce } from "../utils/hooks/useDebounce";
import { useEtaxPdfDownload } from "../utils/hooks/useEtaxPdfDownload";
import { useEtaxSignedPdf } from "../utils/hooks/useEtaxSignedPdf";
import { useEtaxWithdraw } from "../utils/hooks/useEtaxWithdraw";
import { useEtaxAutoSendActions } from "../utils/hooks/useEtaxAutoSendActions";
import { useEtaxTransientRetryMax } from "../utils/hooks/useEtaxTransientRetryMax";
import { COMPACT_QUERY } from "../utils/hooks/useCompact";
import ETaxPagination, {
  clampPage,
  parseLimitParam,
  parsePageParam,
} from "../components/ETax/Pagination";
import DocumentFilter from "../components/ETax/DocumentFilter";
import {
  PAYMENT_TYPE,
  CHANNEL,
  CHANNEL_LABEL,
  canWithdrawSubmission,
  getSignedPdfTooltip,
  getWithdrawTooltip,
  resolveEtaxStatusFilter,
  CHANNEL_FILTER,
  DOC_TYPE_FILTER,
} from "../components/ETax/documentMeta";
import CancelSubmissionModal from "../components/ETax/CancelSubmissionModal";
import { getEtaxBreadcrumbLabel } from "../components/ETax/etaxTitle";
import {
  DocTypeChip,
  BillStatusDot,
  SummaryDetailCard,
  ETaxStatusCountSummary,
  LoadErrorState,
  headCellSx,
} from "../components/ETax/documentCells";
import {
  buildDateRangeSearch,
  resolveDateRangeParams,
} from "../components/ETax/dateRangeParams";
import { EtaxDocumentStatusCell } from "../components/ETax/AutoSend/autoSendCells";
import AutoSendNoteDialog from "../components/ETax/AutoSend/AutoSendNoteDialog";
import {
  canAcknowledgeDocument,
  canHoldDocument,
} from "../components/ETax/AutoSend/autoSendMeta";

const compact = "@media " + COMPACT_QUERY;
const LIMIT = 10;
const COLUMN_COUNT = 14;

const EMPTY_TOTALS = {
  amount_before_vat: 0,
  vat_amount: 0,
  net_total: 0,
};

const EMPTY_STATUS_COUNTS: ETaxDocumentStatusCounts = {
  success: 0,
  in_progress: 0,
  pending: 0,
  failed: 0,
};

/**
 * Filter + paging state as stored in the URL. `channel` is taken by the paper drill-down scope
 * (the dashboard card's link), so the ช่องทาง control is stored as `channel_filter`.
 */
interface ListUrlState {
  documentType: string;
  etaxStatus: string;
  branchCode: string;
  routeCode: string;
  customerName: string;
  taxId: string;
  channel: string;
  page: number;
  limit: number;
}

const URL_KEYS = {
  documentType: "document_type",
  etaxStatus: "etax_status",
  branchCode: "branch_code",
  routeCode: "route_code",
  customerName: "customer_name",
  taxId: "tax_id",
  channel: "channel_filter",
  page: "page",
  limit: "limit",
} as const;

const oneOf = (value: string | null, options: { value: string }[]): string =>
  value && options.some((option) => option.value === value) ? value : "all";

const readListUrlState = (params: URLSearchParams): ListUrlState => ({
  documentType: oneOf(params.get(URL_KEYS.documentType), DOC_TYPE_FILTER),
  // The dashboard's alert cards link here with `etax_status` so the list opens on exactly the
  // rows the card counted.
  etaxStatus: resolveEtaxStatusFilter(params.get(URL_KEYS.etaxStatus)),
  // Branch and route lists load asynchronously, so their values are taken as given.
  branchCode: params.get(URL_KEYS.branchCode) || "all",
  routeCode: params.get(URL_KEYS.routeCode) || "all",
  customerName: params.get(URL_KEYS.customerName) ?? "",
  taxId: params.get(URL_KEYS.taxId) ?? "",
  channel: oneOf(params.get(URL_KEYS.channel), CHANNEL_FILTER),
  page: parsePageParam(params.get(URL_KEYS.page)),
  limit: parseLimitParam(params.get(URL_KEYS.limit), LIMIT),
});

/** The URL for a list state: defaults are left out so a plain link stays plain. */
const writeListUrlState = (
  params: URLSearchParams,
  state: ListUrlState,
): URLSearchParams => {
  const next = new URLSearchParams(params);
  const put = (key: string, value: string, isDefault: boolean) => {
    if (isDefault) next.delete(key);
    else next.set(key, value);
  };
  put(URL_KEYS.documentType, state.documentType, state.documentType === "all");
  put(URL_KEYS.etaxStatus, state.etaxStatus, state.etaxStatus === "all");
  put(URL_KEYS.branchCode, state.branchCode, state.branchCode === "all");
  put(URL_KEYS.routeCode, state.routeCode, state.routeCode === "all");
  put(URL_KEYS.customerName, state.customerName, state.customerName === "");
  put(URL_KEYS.taxId, state.taxId, state.taxId === "");
  put(URL_KEYS.channel, state.channel, state.channel === "all");
  put(URL_KEYS.page, String(state.page), state.page === 1);
  put(URL_KEYS.limit, String(state.limit), state.limit === LIMIT);
  return next;
};

const renderSkeletonRows = () =>
  Array.from({ length: LIMIT }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));

export default function ETaxDetailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFromParam = searchParams.get("date_from");
  const dateToParam = searchParams.get("date_to");
  // cash-paid documents. It is a fixed drill-down scope (no filter control), carried
  // straight through to the API.
  const paymentType = searchParams.get("payment_type") ?? "";
  // The paper card links here with `channel=PAPER`. `payment_type=cashsale` is the older
  // spelling of the same drill-down and is still honoured, so a bookmarked or in-flight
  // link from before the change keeps landing on the paper list.
  const channelParam = searchParams.get("channel") ?? "";
  const isPaperScope =
    channelParam === CHANNEL.PAPER || paymentType === PAYMENT_TYPE.CASH;
  // Memoised so the parsed Dayjs values stay referentially stable across renders
  // (otherwise fetchDocuments would change every render and re-fetch in a loop).
  // An inverted pair from a hand-edited link is swapped.
  const { from: startDate, to: endDate } = useMemo(
    () => resolveDateRangeParams(dateFromParam, dateToParam),
    [dateFromParam, dateToParam],
  );
  const dateSearch = buildDateRangeSearch(startDate, endDate);

  // Every filter and the page are seeded from the URL and written back to it, so the back
  // button, a reload or a shared link reopen the list exactly as it was.
  const [initialUrlState] = useState<ListUrlState>(() => readListUrlState(searchParams));

  const [page, setPage] = useState<number>(initialUrlState.page);
  const [limit, setLimit] = useState<number>(initialUrlState.limit);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [documents, setDocuments] = useState<ETaxDocument[]>([]);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [statusCounts, setStatusCounts] =
    useState<ETaxDocumentStatusCounts>(EMPTY_STATUS_COUNTS);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<boolean>(false);

  const { downloadingId, downloadPdf } = useEtaxPdfDownload();
  const { signedPdfBusyId, viewSignedPdf } = useEtaxSignedPdf();
  const retryMax = useEtaxTransientRetryMax();

  const [documentType, setDocumentType] = useState<string>(initialUrlState.documentType);
  const [etaxStatus, setEtaxStatus] = useState<string>(initialUrlState.etaxStatus);
  const [branchCode, setBranchCode] = useState<string>(initialUrlState.branchCode);
  const [routeCode, setRouteCode] = useState<string>(initialUrlState.routeCode);
  const [customerName, setCustomerName] = useState<string>(initialUrlState.customerName);
  const [taxId, setTaxId] = useState<string>(initialUrlState.taxId);
  const [channel, setChannel] = useState<string>(initialUrlState.channel);

  // ---- URL sync ----
  // State -> URL runs only when the state changes; URL -> state only when the URL differs from
  // what this page last wrote (back / forward, or a link to this same route). Keeping the two
  // triggers apart is what stops one from writing stale values over the other.
  const searchString = searchParams.toString();
  const lastWrittenSearch = useRef<string>(searchString);
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  useEffect(() => {
    const current = searchParamsRef.current;
    const next = writeListUrlState(current, {
      documentType,
      etaxStatus,
      branchCode,
      routeCode,
      customerName,
      taxId,
      channel,
      page,
      limit,
    });
    // The range is written back normalised (a swapped pair reads the right way round).
    next.delete("date_from");
    next.delete("date_to");
    new URLSearchParams(dateSearch.slice(1)).forEach((value, key) => next.set(key, value));
    const nextString = next.toString();
    if (nextString !== current.toString()) {
      lastWrittenSearch.current = nextString;
      setSearchParamsRef.current(next, { replace: true });
    }
  }, [
    documentType,
    etaxStatus,
    branchCode,
    routeCode,
    customerName,
    taxId,
    channel,
    page,
    limit,
    dateSearch,
  ]);

  useEffect(() => {
    if (searchString === lastWrittenSearch.current) return;
    lastWrittenSearch.current = searchString;
    const fromUrl = readListUrlState(new URLSearchParams(searchString));
    setDocumentType(fromUrl.documentType);
    setEtaxStatus(fromUrl.etaxStatus);
    setBranchCode(fromUrl.branchCode);
    setRouteCode(fromUrl.routeCode);
    setCustomerName(fromUrl.customerName);
    setTaxId(fromUrl.taxId);
    setChannel(fromUrl.channel);
    setPage(fromUrl.page);
    setLimit(fromUrl.limit);
  }, [searchString]);

  const customerNameSearch = useDebounce(customerName, 500);
  const taxIdSearch = useDebounce(taxId, 500);

  // Only the newest request may write state. Changing the date range fires a request
  // per picker edit; without this guard a slower earlier response can land last and
  // repaint the table with rows from the previous filter.
  const requestSeq = useRef<number>(0);

  const fetchDocuments = useCallback(async () => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;

    setLoading(true);
    setLoadError(false);
    try {
      const filter: ETaxDocumentFilter = {
        dateFrom: startDate,
        dateTo: endDate,
        documentType,
        etaxStatus,
        paymentType,
        // A scoped drill-down states its own channel and the control is hidden; anywhere
        // else the filter decides. The two can never disagree because only one exists.
        channel: isPaperScope ? CHANNEL.PAPER : channel,
        branchCode,
        routeCode,
        customerName: customerNameSearch,
        taxId: taxIdSearch,
      };
      const {
        data: {
          data: { rows, count, totals: rowTotals, status_counts },
        },
      } = await getETaxDocuments(page, limit, filter);

      if (requestSeq.current !== seq) return;
      setDocuments(rows);
      setTotalCount(count);
      setTotals(rowTotals);
      setStatusCounts(status_counts);
      // After a hold release / acknowledgement / withdrawal moved the last row of the last
      // page out of this filter, step back to the last page that still has rows.
      const clamped = clampPage(page, count, limit);
      if (clamped !== page) setPage(clamped);
    } catch {
      // The axios interceptor surfaces the error toast; show the load error rather than
      // an empty table that reads as "no documents".
      if (requestSeq.current !== seq) return;
      setDocuments([]);
      setTotalCount(0);
      setTotals(EMPTY_TOTALS);
      setStatusCounts(EMPTY_STATUS_COUNTS);
      setLoadError(true);
    } finally {
      if (requestSeq.current === seq) setLoading(false);
    }
  }, [
    page,
    limit,
    startDate,
    endDate,
    documentType,
    etaxStatus,
    paymentType,
    channel,
    isPaperScope,
    branchCode,
    routeCode,
    customerNameSearch,
    taxIdSearch,
  ]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const {
    withdrawTarget,
    withdrawing,
    askWithdraw,
    closeWithdraw,
    confirmWithdraw,
  } = useEtaxWithdraw(fetchDocuments);

  const {
    noteTarget,
    noteSubmitting,
    askHold,
    askAcknowledge,
    closeNote,
    confirmNote,
    releasingId,
    releaseHold,
  } = useEtaxAutoSendActions(fetchDocuments);

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        {/* Carry the active range back to the dashboard so its cards keep summarising
            exactly what this list shows. */}
        <Link
          to={`/e-tax${dateSearch}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {getEtaxBreadcrumbLabel()}
          </Typography>
        </Link>
        <Typography variant="body1" sx={{ mx: 1 }}>
          /
        </Typography>
        <Typography variant="body1" color="primary">
          รายละเอียดรายการ{isPaperScope ? " (ส่งแบบกระดาษ)" : ""}
        </Typography>
      </Box>

      <Paper>
        <DocumentFilter
          documentType={documentType}
          setDocumentType={setDocumentType}
          etaxStatus={etaxStatus}
          setEtaxStatus={setEtaxStatus}
          branchCode={branchCode}
          setBranchCode={setBranchCode}
          routeCode={routeCode}
          setRouteCode={setRouteCode}
          customerName={customerName}
          setCustomerName={setCustomerName}
          taxId={taxId}
          setTaxId={setTaxId}
          {...(isPaperScope ? {} : { channel, setChannel })}
          onChange={() => setPage(1)}
        />

        <Box
          display="flex"
          alignItems="center"
          gap={2}
          mb={2}
          sx={{ [compact]: { flexWrap: "wrap" } }}
        >
          <SummaryDetailCard
            title="มูลค่าสินค้า"
            value={`${formatNumber(totals.amount_before_vat)} บาท`}
          />
          <SummaryDetailCard
            title="ภาษีมูลค่าเพิ่ม (VAT)"
            value={`${formatNumber(totals.vat_amount)} บาท`}
          />
          <SummaryDetailCard
            title="รวมทั้งสิ้น"
            value={`${formatNumber(totals.net_total)} บาท`}
          />
          <ETaxStatusCountSummary count={totalCount} counts={statusCounts} />
        </Box>

        <TableContainer sx={{ borderRadius: "12px" }}>
          <Table>
            <TableHead sx={{ bgcolor: (theme) => theme.palette.background.default }}>
              <TableRow>
                <TableCell sx={headCellSx}>#</TableCell>
                <TableCell sx={headCellSx}>สร้าง</TableCell>
                <TableCell sx={headCellSx}>เลขที่บิล</TableCell>
                <TableCell sx={headCellSx}>Tax ID</TableCell>
                <TableCell sx={headCellSx}>ชื่อลูกค้า</TableCell>
                <TableCell sx={headCellSx}>ประเภท</TableCell>
                {/* The list can now hold both kinds at once — cash no longer means paper —
                    so a row has to say which one it is. */}
                <TableCell sx={headCellSx}>ช่องทาง</TableCell>
                <TableCell sx={headCellSx}>สนญ.</TableCell>
                <TableCell align="right" sx={headCellSx}>
                  มูลค่าสินค้า
                </TableCell>
                <TableCell align="right" sx={headCellSx}>
                  ภาษีมูลค่าเพิ่ม (VAT)
                </TableCell>
                <TableCell align="right" sx={headCellSx}>
                  รวมทั้งสิ้น
                </TableCell>
                <TableCell sx={headCellSx}>สถานะบิล</TableCell>
                <TableCell sx={headCellSx}>สถานะ</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && renderSkeletonRows()}
              {!loading && loadError && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                    <LoadErrorState onRetry={fetchDocuments} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && !loadError && documents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary" fontWeight={500}>
                      ไม่พบข้อมูล
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                documents.map((doc, index) => {
                  // A PDF only exists once the e-tax item has been generated
                  // (path_file is set). Pending / failed / in-progress rows have
                  // no file yet, so the download action stays disabled for them.
                  const canDownload = Boolean(doc.etax_uuid && doc.path_file);
                  const isDownloading =
                    downloadingId !== null && downloadingId === doc.etax_uuid;
                  const isViewingSigned =
                    signedPdfBusyId !== null && signedPdfBusyId === doc.etax_uuid;
                  return (
                    <TableRow
                      key={doc.transaction_uuid}
                      sx={{
                        "& td, & th": { padding: "8px" },
                        bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                      }}
                    >
                      <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                      <TableCell>{formatDate(doc.transaction_date)}</TableCell>
                      <TableCell>{doc.transaction_no}</TableCell>
                      <TableCell>{doc.tax_id ?? "-"}</TableCell>
                      <TableCell>{doc.customer_name}</TableCell>
                      <TableCell>
                        <DocTypeChip type={doc.document_type} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.etax_enabled
                            ? CHANNEL_LABEL[CHANNEL.ETAX]
                            : CHANNEL_LABEL[CHANNEL.PAPER]}
                        </Typography>
                      </TableCell>
                      <TableCell>{doc.branch_code ?? "-"}</TableCell>
                      <TableCell align="right">
                        {formatNumber(doc.amount_before_vat)}
                      </TableCell>
                      <TableCell align="right">{formatNumber(doc.vat_amount)}</TableCell>
                      <TableCell align="right">{formatNumber(doc.net_total)}</TableCell>
                      <TableCell>
                        <BillStatusDot status={doc.bill_status} />
                      </TableCell>
                      <TableCell>
                        {/* The chip carries the lifecycle wording itself; the lines under it
                            are the weekly auto-send facts (hold / next round / who sent). */}
                        <EtaxDocumentStatusCell doc={doc} retryMax={retryMax} />
                      </TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip
                            title={
                              canDownload ? "ดาวน์โหลด PDF" : "ยังไม่มีไฟล์ให้ดาวน์โหลด"
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!canDownload || downloadingId !== null}
                                onClick={() =>
                                  downloadPdf(doc.etax_uuid, `${doc.transaction_no}.pdf`)
                                }
                              >
                                {isDownloading ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <DownloadIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={getSignedPdfTooltip(doc.document_status)}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={
                                  !doc.has_signed_document || signedPdfBusyId !== null
                                }
                                onClick={() => viewSignedPdf(doc.etax_uuid)}
                              >
                                {isViewingSigned ? (
                                  <CircularProgress size={18} />
                                ) : (
                                  <VerifiedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* Withdrawing is offered on every sent row and disabled with a
                              reason on the rest, rather than hidden: a document that can no
                              longer be pulled back is exactly the case an operator needs
                              told about, and a button that vanishes tells them nothing. */}
                          <Tooltip
                            title={getWithdrawTooltip(
                              doc.etax_status,
                              doc.document_status,
                              doc.can_withdraw,
                            )}
                          >
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={
                                  !canWithdrawSubmission(
                                    doc.etax_status,
                                    doc.document_status,
                                    doc.can_withdraw,
                                  )
                                }
                                onClick={() =>
                                  askWithdraw({
                                    transactionUuid: doc.transaction_uuid,
                                    transactionNo: doc.transaction_no,
                                    customerName: doc.customer_name,
                                    documentStatus: doc.document_status,
                                  })
                                }
                              >
                                <CancelScheduleSendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {/* Weekly auto-send: keep an unsent bill out of the round, or let
                              it back in. Only unsent bills have a round to be kept out of. */}
                          {canHoldDocument(doc) &&
                            (doc.is_held ? (
                              <Tooltip title="ยกเลิกไม่ส่งอัตโนมัติ">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={releasingId !== null}
                                    onClick={() => releaseHold(doc)}
                                  >
                                    {releasingId === doc.transaction_uuid ? (
                                      <CircularProgress size={18} />
                                    ) : (
                                      <PlayCircleOutlineIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : (
                              <Tooltip title="ไม่ส่งอัตโนมัติ">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => askHold(doc)}
                                  >
                                    <PauseCircleOutlineIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ))}
                          {canAcknowledgeDocument(doc) && (
                            <Tooltip title="รับทราบ">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => askAcknowledge(doc)}
                                >
                                  <TaskAltIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        <ETaxPagination
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
        />
      </Paper>

      <CancelSubmissionModal
        target={withdrawTarget}
        loading={withdrawing}
        onCancel={closeWithdraw}
        onConfirm={confirmWithdraw}
      />

      <AutoSendNoteDialog
        target={noteTarget}
        loading={noteSubmitting}
        onClose={closeNote}
        onConfirm={confirmNote}
      />
    </Box>
  );
}
